/**
 * SetLogger — inline set entry for the active exercise.
 *
 * Stepper-based: custom +/- buttons for weight and reps so touch targets
 * are large and consistent across iOS Safari. Defaults come from the
 * payload's suggestedNextSet (first working set uses c2/c3; sets 2+
 * inherit session-locked weight). RPE and warmup are optional.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { MinusIcon, PlusIcon, CheckIcon } from '@phosphor-icons/react';
import { PlateBreakdown } from './PlateBreakdown';

export interface SuggestedNextSet {
  exerciseId: string;
  weightKg: number | null;
  reps: string | null;
  plates: { kg: number; sidePairs: number }[] | null;
}

interface Props {
  sessionId: number;
  exerciseId: string;
  suggested: SuggestedNextSet | null;
  onLogged: (payload: unknown) => void;
}

function parseTopRep(repTarget: string | null): number {
  if (!repTarget) return 8;
  // "8-12" → 12; "10" → 10; "AMRAP" → fallback 8.
  const m = repTarget.match(/(\d+)\s*(?:-\s*(\d+))?/);
  if (!m) return 8;
  return Number(m[2] ?? m[1]);
}

const WEIGHT_STEP = 2.5;
const REP_STEP = 1;

export function SetLogger({ sessionId, exerciseId, suggested, onLogged }: Props) {
  const defaultWeight = suggested?.weightKg ?? null;
  const defaultReps = useMemo(
    () => parseTopRep(suggested?.reps ?? null),
    [suggested?.reps],
  );

  const [weightKg, setWeightKg] = useState<number | null>(defaultWeight);
  const [editingWeight, setEditingWeight] = useState(false);
  const [weightDraft, setWeightDraft] = useState('');
  const [reps, setReps] = useState<number>(defaultReps);
  const [rpe, setRpe] = useState<number | null>(null);
  const [warmup, setWarmup] = useState(false);
  const [bfr, setBfr] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  // When the active exercise changes (or suggested updates), reset defaults.
  useEffect(() => {
    setWeightKg(defaultWeight);
    setEditingWeight(false);
    setWeightDraft('');
    setReps(defaultReps);
    setRpe(null);
    setWarmup(false);
    setBfr(false);
    setNotes('');
    setNotesOpen(false);
    setStatus(null);
    setError(null);
  }, [exerciseId, defaultWeight, defaultReps]);

  const bumpWeight = useCallback((delta: number) => {
    setWeightKg((w) => {
      const next = (w ?? 0) + delta;
      return next < 0 ? 0 : Math.round(next * 4) / 4;
    });
  }, []);

  const startEditWeight = useCallback(() => {
    setWeightDraft(weightKg == null ? '' : String(weightKg));
    setEditingWeight(true);
  }, [weightKg]);

  const commitWeight = useCallback(() => {
    const trimmed = weightDraft.trim();
    if (trimmed === '') {
      // Empty commits as bodyweight (no external load).
      setWeightKg(null);
    } else {
      const n = Number(trimmed);
      if (Number.isFinite(n) && n >= 0) {
        // Snap to a 0.25 kg grid — fine enough for any machine or free
        // weight, without letting float noise through.
        setWeightKg(Math.round(n * 4) / 4);
      }
    }
    setEditingWeight(false);
  }, [weightDraft]);

  const bumpReps = useCallback((delta: number) => {
    setReps((r) => Math.max(1, r + delta));
  }, []);

  const bumpRpe = useCallback((delta: number) => {
    setRpe((r) => {
      const next = (r ?? 7) + delta;
      if (next < 1) return 1;
      if (next > 10) return 10;
      return Math.round(next * 2) / 2;
    });
  }, []);

  async function handleSubmit() {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/tools/gym/session/${sessionId}/sets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          exerciseId,
          weightKg: weightKg ?? undefined,
          reps,
          rpe: rpe ?? undefined,
          warmup: warmup || undefined,
          bfr: bfr || undefined,
          notes: notes.trim() ? notes.trim() : undefined,
        }),
      });
      const json = (await res.json().catch(() => null)) as {
        payload?: unknown;
        error?: string;
      } | null;
      if (!res.ok) {
        setError(json?.error ?? 'log_failed');
        return;
      }
      onLogged(json?.payload);
      setStatus(warmup ? 'warmup logged' : 'set logged');
      setRpe(null);
      setWarmup(false);
      setBfr(false);
      setNotes('');
      setNotesOpen(false);
    } catch {
      setError('log_failed');
    } finally {
      setSubmitting(false);
    }
  }

  const weightDisplay = weightKg == null ? 'BW' : `${weightKg} kg`;

  return (
    <div className="set-logger">
      <div className="set-logger-row">
        <span className="set-logger-label">weight</span>
        <button
          type="button"
          className="stepper-btn"
          onClick={() => bumpWeight(-WEIGHT_STEP)}
          aria-label="decrease weight"
        >
          <MinusIcon size={14} weight="bold" />
        </button>
        {editingWeight ? (
          <input
            type="number"
            inputMode="decimal"
            step="any"
            autoFocus
            className="set-logger-value set-logger-value-input"
            value={weightDraft}
            onChange={(e) => setWeightDraft(e.target.value)}
            onBlur={commitWeight}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitWeight();
              }
            }}
            placeholder="BW"
            aria-label="weight in kg"
          />
        ) : (
          <button
            type="button"
            className="set-logger-value set-logger-value-edit"
            onClick={startEditWeight}
            aria-label="edit weight"
          >
            {weightDisplay}
          </button>
        )}
        <button
          type="button"
          className="stepper-btn"
          onClick={() => bumpWeight(WEIGHT_STEP)}
          aria-label="increase weight"
        >
          <PlusIcon size={14} weight="bold" />
        </button>
      </div>

      {weightKg != null && weightKg > 0 && (
        <PlateBreakdown sessionId={sessionId} exerciseId={exerciseId} weightKg={weightKg} />
      )}

      <div className="set-logger-row">
        <span className="set-logger-label">reps</span>
        <button
          type="button"
          className="stepper-btn"
          onClick={() => bumpReps(-REP_STEP)}
          aria-label="decrease reps"
        >
          <MinusIcon size={14} weight="bold" />
        </button>
        <span className="set-logger-value">{reps}</span>
        <button
          type="button"
          className="stepper-btn"
          onClick={() => bumpReps(REP_STEP)}
          aria-label="increase reps"
        >
          <PlusIcon size={14} weight="bold" />
        </button>
      </div>

      <div className="set-logger-row">
        <span className="set-logger-label">RPE</span>
        <button
          type="button"
          className="stepper-btn"
          onClick={() => bumpRpe(-0.5)}
          aria-label="decrease RPE"
        >
          <MinusIcon size={14} weight="bold" />
        </button>
        <span className="set-logger-value">{rpe ?? '—'}</span>
        <button
          type="button"
          className="stepper-btn"
          onClick={() => bumpRpe(0.5)}
          aria-label="increase RPE"
        >
          <PlusIcon size={14} weight="bold" />
        </button>
      </div>

      <div className="set-logger-pills">
        <button
          type="button"
          className={`set-logger-pill${warmup ? ' set-logger-pill-on' : ''}`}
          onClick={() => setWarmup((v) => !v)}
          aria-pressed={warmup}
        >
          warmup
        </button>
        <button
          type="button"
          className={`set-logger-pill${bfr ? ' set-logger-pill-on' : ''}`}
          onClick={() => setBfr((v) => !v)}
          aria-pressed={bfr}
        >
          BFR
        </button>
        {!notesOpen ? (
          <button
            type="button"
            className="set-logger-pill set-logger-pill-note"
            onClick={() => setNotesOpen(true)}
            aria-label="add note"
          >
            note +
          </button>
        ) : null}
      </div>

      {notesOpen && (
        <textarea
          className="set-logger-notes"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="felt easy / elbow twingy / …"
        />
      )}

      <div className="set-logger-row set-logger-toggles">
        <span />
        <button
          type="button"
          className="set-logger-submit"
          onClick={handleSubmit}
          disabled={submitting}
        >
          <CheckIcon size={14} weight="bold" />
          {submitting ? 'logging…' : 'log set'}
        </button>
      </div>

      {status && <p className="set-logger-status">{status}</p>}
      {error && <p className="set-logger-error">could not log set ({error})</p>}
    </div>
  );
}
